/**
 * Edit form for an activity.
 */
import * as React from 'react';
import { FileText } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Input } from '@/components/forms/Input';
import { Select } from '@/components/forms/Select';
import { getActivity, updateActivity } from '@/services/activities.service';
import type { UpdateActivityDto } from '@/types/activity.types';
import { useFormValidation } from '@/hooks/useFormValidation';
import { activityConfigValidationSchema } from '@/utils/validation-schemas-utils';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { ACTIVITY_TYPES_QUERY_KEY } from './activity-configs';
import {
  activityToFormData,
  applyActivityFormField,
  buildActivitySubmitDto,
  fetchActivityTypes,
  mapActivityTypeOptions,
} from './activity-form-utils';

/** Props for {@link EditActivityView}. */
interface EditActivityViewProps {
  activityId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

/** Edit form for an activity. */
export const EditActivityView: React.FC<EditActivityViewProps> = ({
  activityId,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = React.useState<UpdateActivityDto>({});
  const validation = useFormValidation(activityConfigValidationSchema);

  const {
    data: activity,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['activity', activityId],
    queryFn: () => getActivity(activityId),
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError,
    error,
    catalogKey: 'activityNotFound',
    onClose,
    entityId: activityId,
  });

  const { data: activityTypes = [] } = useQuery({
    queryKey: ACTIVITY_TYPES_QUERY_KEY,
    queryFn: fetchActivityTypes,
  });

  const activityTypeOptions = React.useMemo(
    () => mapActivityTypeOptions(activityTypes),
    [activityTypes],
  );

  /** Hydrate the form once GET activity resolves. */
  React.useEffect(() => {
    if (!activity) return;
    const initialData = activityToFormData(activity);
    setFormData(initialData);
    validation.validateInitialFormData(initialData);
  }, [activity]);

  const updateMutation = useMutation({
    mutationFn: (dto: UpdateActivityDto) => updateActivity(activityId, dto),
    onSuccess: () => {
      toast.success('Activité modifiée avec succès !');
      onSuccess?.();
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  /** Patch one field and run the shared validation schema. */
  const handleFieldChange = (
    field: keyof UpdateActivityDto,
    value: string | number | boolean | undefined,
  ) => {
    const updatedData = applyActivityFormField(formData, field, value);
    setFormData(updatedData);
    validation.validateField(field, value, updatedData);
    validation.markAsTouched(field);
    validation.markAsDirty(field);
  };

  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  /** Validate then PATCH with a denormalized type label. */
  const handleSubmit = () => {
    if (!validation.validateForm(formData)) return;
    updateMutation.mutate(buildActivitySubmitDto(formData, activityTypes));
  };

  return (
    <SlidePanelForm
      formType="edit"
      title="Modifier l'activité"
      subtitle="Modifiez les informations de l'activité"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Enregistrer"
      isSubmitting={updateMutation.isPending}
      isValid={validation.isFormValid()}
      isLoading={isLoading || (!!activity && Object.keys(formData).length === 0)}
    >
      <div className="form-section">
        <h4 className="form-section-title">
          <FileText className="w-4 h-4" />
          Informations générales
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Code"
            value={formData.code || ''}
            onChange={(e) => handleFieldChange('code', e.target.value)}
            onBlur={() => handleBlur('code')}
            placeholder="Ex: AIDE_DEVOIRS_6EME"
            maxLength={50}
            required
            helperText="Code unique identifiant l'activité"
            state={validation.getFieldState('code')}
            error={validation.getFieldError('code')}
          />

          <Input
            label="Libellé"
            value={formData.label || ''}
            onChange={(e) => handleFieldChange('label', e.target.value)}
            onBlur={() => handleBlur('label')}
            placeholder="Ex: Aide aux devoirs niveau 6ème"
            maxLength={255}
            required
            helperText="Nom complet de l'activité"
            state={validation.getFieldState('label')}
            error={validation.getFieldError('label')}
          />
        </div>

        <Select
          label="Type d'activité"
          value={formData.activityTypeId || ''}
          onChange={(value) => handleFieldChange('activityTypeId', value)}
          onBlur={() => handleBlur('activityTypeId')}
          options={activityTypeOptions}
          placeholder="Sélectionner un type"
          required
          state={validation.getFieldState('activityTypeId')}
          error={validation.getFieldError('activityTypeId')}
        />

        <Input
          label="Description"
          value={formData.description || ''}
          onChange={(e) => handleFieldChange('description', e.target.value)}
          onBlur={() => handleBlur('description')}
          placeholder="Ex: Accompagnement scolaire"
          maxLength={500}
          state={validation.getFieldState('description')}
          error={validation.getFieldError('description')}
        />
      </div>
    </SlidePanelForm>
  );
};
